import * as React from "react";
import { Map as MapIcon } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  riskComparator,
  assignGroup,
  RECOMMENDATION_GROUPS,
  normalizePriorityWeight,
  normalizeScore,
  priorityColor,
  priorityLabelFromWeight,
  calculatePriority,
  GROUP_COLORS,
  type RecommendationGroup,
} from "@/lib/riskOrdering";

export type RoadmapItem = {
  title: string;
  nistFunction: string;
  priority?: "Critical" | "High" | "Medium" | "Low";
  priorityWeight?: string | null;
  score?: number | string | null;
  questionIndex?: number | null;
  maturityLabel?: string | null;
};

type NormalizedRoadmapItem = RoadmapItem & {
  priority_weight: string;
  score: number;
  questionIndex: number;
  priorityBadge: "Critical" | "High" | "Medium" | "Low";
  timelineDays: string;
};

/** Timeline buckets per group – items are spread across these in sorted order */
const GROUP_TIMELINES: Record<RecommendationGroup, string[]> = {
  "Critical Actions": ["Immediate", "30 days", "60 days"],
  "High Priority Actions": ["30 days", "60 days", "90 days"],
  "Medium Priority Improvements": ["90 days", "120 days", "180 days"],
  "Maintain & Optimize": ["180 days", "270 days", "365 days"],
};

function deduplicateNormalizeAndSort(items: RoadmapItem[]): NormalizedRoadmapItem[] {
  const seen = new Set<string>();
  const normalized: NormalizedRoadmapItem[] = [];

  items.forEach((item, idx) => {
    const key = item.title.toLowerCase().trim();
    if (seen.has(key)) return;
    seen.add(key);

    // Use centralized residual-risk priority (inherent weight + maturity)
    const adjPriority = calculatePriority(item.priorityWeight, item.maturityLabel);
    const score = normalizeScore(item.score, 50);
    const questionIndex = item.questionIndex ?? idx;

    normalized.push({
      ...item,
      priority_weight: adjPriority,
      score,
      questionIndex,
      priorityBadge: priorityLabelFromWeight(adjPriority),
      timelineDays: "", // assigned after grouping
    });
  });

  return [...normalized].sort((a, b) =>
    riskComparator(
      { priorityWeight: a.priority_weight, score: a.score, questionIndex: a.questionIndex },
      { priorityWeight: b.priority_weight, score: b.score, questionIndex: b.questionIndex },
    )
  );
}

/** Spread items across the group's timeline buckets proportionally */
function assignTimelines(groupItems: NormalizedRoadmapItem[], groupName: RecommendationGroup) {
  const buckets = GROUP_TIMELINES[groupName];
  const count = groupItems.length;
  groupItems.forEach((item, idx) => {
    const bucketIdx = Math.min(Math.floor((idx / count) * buckets.length), buckets.length - 1);
    item.timelineDays = buckets[bucketIdx];
  });
}

export function RemediationRoadmapSection({ items }: { items: RoadmapItem[] }) {
  const orderedItems = deduplicateNormalizeAndSort(items);
  if (!orderedItems.length) return null;

  const groups = new Map<RecommendationGroup, NormalizedRoadmapItem[]>();
  orderedItems.forEach((item) => {
    const group = assignGroup(item.priority_weight, item.maturityLabel);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(item);
  });

  // Assign cascading per-item timelines within each group
  RECOMMENDATION_GROUPS.forEach((groupName) => {
    const groupItems = groups.get(groupName);
    if (groupItems?.length) assignTimelines(groupItems, groupName);
  });

  const sections: Array<{ groupName: RecommendationGroup; items: NormalizedRoadmapItem[] }> = [];
  let remaining = 10;

  RECOMMENDATION_GROUPS.forEach((groupName) => {
    if (remaining <= 0) return;
    const groupItems = groups.get(groupName);
    if (!groupItems?.length) return;

    const visibleItems = groupItems.slice(0, remaining);
    remaining -= visibleItems.length;

    sections.push({ groupName, items: visibleItems });
  });

  return (
    <section className="remediation-section">
      <h2 className="text-base font-bold mb-1 flex items-center gap-2">
        <MapIcon className="h-4 w-4 text-primary" />
        Remediation Roadmap
      </h2>
      <p className="text-xs text-muted-foreground mb-3">
        The roadmap below provides a prioritized view of recommended remediation
        activities based on the assessment findings. These timelines represent
        typical implementation sequencing used by organizations strengthening
        their cybersecurity posture.
      </p>

      {sections.map(({ groupName, items: sectionItems }) => (
        <div key={groupName} className={`mb-4 rounded-lg border priority-group ${GROUP_COLORS[groupName].border} p-3`}>
          <h3 className={`text-sm font-bold uppercase tracking-wide pb-1 mb-2 border-b priority-group-header ${GROUP_COLORS[groupName].text} ${GROUP_COLORS[groupName].border}`}>
            {groupName}
          </h3>
          <Table>
            <TableHeader>
              <TableRow className="text-xs">
                <TableHead className="w-[80px]">Priority</TableHead>
                <TableHead>Recommendation</TableHead>
                <TableHead className="w-[120px]">NIST Function</TableHead>
                <TableHead className="w-[110px]">Timeline</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sectionItems.map((item) => (
                <TableRow key={`${item.title}-${item.questionIndex}`} className="text-sm">
                  <TableCell>
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${priorityColor(item.priorityBadge)}`}
                    >
                      {item.priorityBadge}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">{item.title}</TableCell>
                  <TableCell className="text-muted-foreground">{item.nistFunction}</TableCell>
                  <TableCell className="text-muted-foreground">{item.timelineDays}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </section>
  );
}
